import React from "react";
import { formatARR } from "../utils/arr";

function getItems(summary) {
  return [
    ["📊 Total ARR", formatARR(summary.totalARR)],
    ["🚀 Outbound ARR", formatARR(summary.outboundARR)],
    ["📥 Inbound ARR", formatARR(summary.inboundARR)],
    ["🏆 Top Outbound", summary.topOutboundPerformer?.name || "N/A"],
    ["⭐ Top Inbound", summary.topInboundPerformer?.name || "N/A"]
  ];
}

function TickerSequence({ items, sequenceRef }) {
  return (
    <div className="arr-ticker-sequence" ref={sequenceRef}>
      {items.map(([label, value]) => (
        <span className="arr-ticker-item" key={label}>
          {label}: {value}
        </span>
      ))}
    </div>
  );
}

export default function ArrTicker({ summary, loading = false, error = false, home = false }) {
  const availableSummary = summary && typeof summary === "object";
  const items = error && !availableSummary
    ? [["ARR summary", "Unavailable"]]
    : loading && !availableSummary
      ? [["Total ARR", "Loading"], ["Outbound ARR", "Loading"], ["Inbound ARR", "Loading"]]
      : getItems(summary || {});
  const tickerRef = React.useRef(null);
  const sequenceRef = React.useRef(null);
  const [loop, setLoop] = React.useState({ distance: 0, copies: 2 });

  React.useLayoutEffect(() => {
    const measure = () => {
      const distance = sequenceRef.current?.getBoundingClientRect().width || 0;
      if (!distance) return;
      // Keep enough complete copies beyond the viewport for the entire loop.
      const copies = Math.max(2, Math.ceil(tickerRef.current.clientWidth / distance) + 1);
      setLoop((current) => current.distance === distance && current.copies === copies
        ? current : { distance, copies });
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(tickerRef.current);
    observer.observe(sequenceRef.current);
    return () => observer.disconnect();
  }, [summary, loading, error]);

  return (
    <div
      className={`arr-ticker${home ? " arr-ticker-home" : ""}`}
      ref={tickerRef}
      role={(loading && !availableSummary) || (error && !availableSummary) ? "status" : undefined}
      aria-label="Monthly ARR ticker"
      title={error && availableSummary ? "Showing the last successfully loaded ARR summary" : undefined}
    >
      <div className="arr-ticker-track" aria-hidden="true"
        style={{ "--arr-ticker-distance": `${loop.distance}px` }}>
        {Array.from({ length: loop.copies }, (_, index) => (
          <TickerSequence key={index} items={items} sequenceRef={index === 0 ? sequenceRef : undefined} />
        ))}
      </div>
      <span className="sr-only">
        {items.map(([label, value]) => `${label}: ${value}`).join(". ")}
      </span>
    </div>
  );
}
